import debug from "src/modules/debug-logger";
import * as selectors from "src/modules/activity-log/duck/selectors";
import * as actions from "src/modules/activity-log/duck/actions";
import environment, { electronOnly } from "src/utils/environment";
import {
  isVideoEvent,
  isPhotoEvent
} from "src/modules/activity-log/supported-events";
import {
  getPathForFileName,
  createMediaDirectoryIfNotExisting,
  clearPendingFiles,
  clearData
} from "src/modules/activity-log/lib/fs-helpers";
import uploadMedia from "./uploadMedia";
import getTimeoutForAttempt from "./getTimeoutForAttempt";
import { store } from "src/store/store";
import createMonitoringUploader from "./createMonitoringUploader";

const log = window.require("electron-log");
const fs = window.require("fs");
const { isBrowser } = environment;

electronOnly(() => {
  createMediaDirectoryIfNotExisting();
});

class ActivityLogUploader {
  constructor() {
    this.getState = store.getState;
    this.dispatch = store.dispatch;
    const pendingAudioEvents = selectors.getPendingAudioEvents(this.getState());
    const pendingCaptureEvents = selectors.getPendingCaptureEvents(
      this.getState()
    );
    this.getResultForFiles = {};
    this.audioUploadQueue = pendingAudioEvents.map((media) => ({
      ...media,
      type: this.mapEventTypeToMediaType(media.eventType),
      attempt: 1,
      finishedWritting: true
    }));
    this.captureUploadQueue = pendingCaptureEvents.map((media) => ({
      ...media,
      type: this.mapEventTypeToMediaType(media.eventType),
      attempt: 1
    }));
    this.liveUploads = {};
    this.audioUploadInProgress = null;
    this.captureUploadInProgress = null;
    this.currentMonitoring = null;
    this.monitoringUploader = createMonitoringUploader({
      dispatch: this.dispatch,
      getState: this.getState,
      getCurrentMonitoringId: () =>
        this.currentMonitoring ? this.currentMonitoring.monitoringId : null
    });

    electronOnly(() => {
      const pendingAudioUpload = this.audioUploadQueue.shift();
      pendingAudioUpload && this.handleUpload(pendingAudioUpload);
      const pendingCaptureUpload = this.captureUploadQueue.shift();
      pendingCaptureUpload && this.handleUpload(pendingCaptureUpload);

      clearPendingFiles({
        filesToPreserve: [...pendingAudioEvents, ...pendingCaptureEvents]
      });
    });
  }

  uploadFinishedMonitoring = async ({
    monitoring,
    lastEvent,
    isFreeVersion
  }) => {
    this.currentMonitoring = monitoring;
    const finishedMonitoring = {
      ...monitoring,
      duration: Date.now() - +monitoring.startDate
    };

    if (lastEvent.eventType === "AWAKE" && !isFreeVersion && !isBrowser) {
      const fileRelatedLength = await this.finishAudioUpload({
        fileName: lastEvent.fileName
      });
      lastEvent.fileRelatedLength = fileRelatedLength;
    }

    this.dispatch(
      actions.eventEnded({
        monitoringId: finishedMonitoring.monitoringId,
        event: lastEvent
      })
    );
    this.dispatch(
      actions.monitoringEnded({
        monitoringId: finishedMonitoring.monitoringId,
        avatarId: finishedMonitoring.avatarId,
        duration: finishedMonitoring.duration
      })
    );
    this.monitoringUploader.upload(finishedMonitoring.monitoringId);
    this.currentMonitoring = null;
  };

  handleUpload = async (media) => {
    if (media.type === "AUDIO") {
      if (this.audioUploadInProgress) {
        this.liveUploads[media.fileName]
          ? this.audioUploadQueue.unshift(media)
          : this.audioUploadQueue.push(media);
        return;
      }
      this.audioUploadInProgress = media;
    } else {
      if (this.captureUploadInProgress) {
        this.liveUploads[media.fileName]
          ? this.captureUploadQueue.unshift(media)
          : this.captureUploadQueue.push(media);
        return;
      }
      this.captureUploadInProgress = media;
    }

    const startNextUpload = () => {
      if (media.type === "AUDIO") {
        this.audioUploadInProgress = null;
        const nextUpload = this.audioUploadQueue.shift();
        if (nextUpload) this.handleUpload(nextUpload);
      } else {
        this.captureUploadInProgress = null;
        const nextUpload = this.captureUploadQueue.shift();
        if (nextUpload) this.handleUpload(nextUpload);
      }
    };

    const runUpload = async () => {
      debug.activityLog(
        "starting upload, current audio que: ",
        this.audioUploadQueue
      );
      const getResult = this.getResultForFiles[media.fileName];
      try {
        if (fs.existsSync(getPathForFileName(media.fileName))) {
          await uploadMedia(media);
          debug.activityLog("File uploaded, fileName", media.fileName);
          if (getResult) getResult({ result: "SUCCESS" });
          this.dispatch(
            actions.eventMediaUploaded({
              fileName: media.fileName,
              monitoringId: media.monitoringId
            })
          );
          fs.unlink(getPathForFileName(media.fileName), (error) => {
            error && log.error(error);
          });
        } else {
          debug.activityLog("File does not exist");
          this.dispatch(
            actions.eventMediaUploaded({
              fileName: media.fileName,
              monitoringId: media.monitoringId
            })
          );
        }
        startNextUpload();
      } catch (error) {
        if (getResult) getResult({ result: "FAILURE" });
        log.error(
          `media upload attempt ${media?.attempt} failed`,
          error || "no error provided"
        );
        if (
          (media.attempt >= 5 &&
            media.finishedWritting &&
            media.type === "AUDIO") ||
          (media.attempt >= 5 && media.type !== "AUDIO")
        ) {
          media.attempt = 1;
          media.type === "AUDIO"
            ? this.audioUploadQueue.push(media)
            : this.captureUploadQueue.push(media);
          startNextUpload();
          return;
        }
        if (navigator.onLine) {
          ++media.attempt;
          await new Promise((resolve) =>
            setTimeout(resolve, getTimeoutForAttempt(media.attempt))
          );
        } else {
          await new Promise((resolve) => {
            media.attempt = 1;
            window.addEventListener("online", resolve, { once: true });
          });
        }
        runUpload();
      }
      if (getResult) {
        delete this.getResultForFiles[media.fileName];
      }
    };
    runUpload();
  };

  clearData = () => electronOnly(() => clearData());

  mapEventTypeToMediaType = (eventType) => {
    if (isVideoEvent(eventType)) return "VIDEO";
    if (isPhotoEvent(eventType)) return "PHOTO";
    return "AUDIO";
  };

  uploadCaptureEvent = (
    { fileName, monitoringId, onSample, eventType },
    getResult
  ) => {
    debug.activityLog(`uploadCaptureEvent called for fileName ${fileName}`);
    const media = {
      fileName,
      monitoringId,
      onSample,
      attempt: 1,
      type: this.mapEventTypeToMediaType(eventType)
    };
    if (getResult) {
      this.getResultForFiles[fileName] = getResult;
    }
    this.handleUpload(media);
  };

  uploadAudioEvent = ({ fileName, monitoringId, onSample, eventType }) => {
    debug.activityLog(`uploadAudioEvent called for fileName ${fileName}`);

    const writeStream = fs.createWriteStream(getPathForFileName(fileName), {
      flags: "w"
    });
    const dataToWrite = [];

    const media = {
      fileName,
      monitoringId,
      writeStream,
      onSample,
      attempt: 1,
      canWrite: false,
      finishedWritting: false,
      type: this.mapEventTypeToMediaType(eventType),
      onData(data) {
        media.canWrite
          ? writeStream.write(data)
          : !media.finishedWritting && dataToWrite.push(data);
      }
    };
    this.liveUploads[fileName] = media;

    writeStream.on("error", (error) => {
      debug.activityLog(
        "ActivityLogUploader > uploadAudioEvent > writeStream.on('error')",
        error
      );
      media.canWrite = false;
      media.finishedWritting = true;
      writeStream.destroy();
      delete this.liveUploads[fileName];
      fs.unlink(getPathForFileName(fileName), (error) => {
        error && log.error(error);
      });
    });
    writeStream.once("open", (fd) => {
      dataToWrite.forEach((data) => writeStream.write(data));
      media.canWrite = true;
      this.handleUpload(media);
    });
    writeStream.once("finish", () => {
      media.finishedWritting = true;
    });
  };

  finishAudioUpload = ({ fileName }) => {
    debug.activityLog(`finishAudioUpload called for fileName ${fileName}`);
    return new Promise((resolve) => {
      const media = this.liveUploads[fileName];
      media.canWrite = false;

      media.writeStream.end(() => {
        debug.activityLog(
          `finished writing file ${fileName}, bytesWritten: ${media.writeStream.bytesWritten}`
        );
        if (media.writeStream.bytesWritten === 0 && environment.debugMode) {
          alert("Audio file had no data");
        }
        delete this.liveUploads[fileName];
        resolve(media.fileRelatedLength);
      });
    });
  };

  onData = ({ data, fileName }) => {
    const media = this.liveUploads[fileName];
    if (media == null) return;
    media.onData(data);
  };
}

let activityLogUploaderInstance;

export function instantiateActivityLogUploader() {
  if (activityLogUploaderInstance == null) {
    activityLogUploaderInstance = new ActivityLogUploader();
  }
  return activityLogUploaderInstance;
}

export function getActivityLogUploader() {
  instantiateActivityLogUploader();
  return activityLogUploaderInstance;
}
